/**
 * PublicContentCard
 * Card renderer for published content on public collection/home pages.
 *
 * This component is presentation-only: fetching/filtering happens in page-level
 * containers (`HomePage`, `CollectionPage`).
 */
import { Link } from 'react-router-dom';
import { summarizeContent } from '../../utils/contentConfig';
import { inferMediaKind } from '../../utils/media';
import { formatDate } from '../../utils/time';

function renderGalleryPreview(item) {
  const mediaKind = inferMediaKind({
    mediaType: item.mediaType,
    mediaUrl: item.mediaUrl,
  });
  const imageSource = item.mediaUrl || item.thumbnailUrl;

  if ((mediaKind === 'image' || mediaKind === 'gif') && imageSource) {
    return (
      <div className="content-card-media-wrap">
        <img
          className="content-card-media"
          src={imageSource}
          alt={item.title || 'Gallery media'}
          loading="lazy"
        />
      </div>
    );
  }

  if (mediaKind === 'video' && item.mediaUrl) {
    return (
      <div className="content-card-media-wrap">
        <video
          className="content-card-media"
          preload="metadata"
          muted
          playsInline
          poster={item.thumbnailUrl || undefined}
          onLoadedData={(event) => {
            // Best-effort first-frame preview without exposing player controls.
            try {
              event.currentTarget.currentTime = 0.1;
              event.currentTarget.pause();
            } catch {
              // No-op fallback for browsers that block seek at this point.
            }
          }}
        >
          <source src={item.mediaUrl} />
        </video>
      </div>
    );
  }

  if (item.thumbnailUrl) {
    return (
      <div className="content-card-media-wrap">
        <img
          className="content-card-media"
          src={item.thumbnailUrl}
          alt={item.title || 'Gallery media'}
          loading="lazy"
        />
      </div>
    );
  }

  return null;
}

function renderCourseContentPreview(item) {
  if (item.previewImageUrl) {
    return (
      <div className="content-card-media-wrap">
        <img
          className="content-card-media"
          src={item.previewImageUrl}
          alt={item.title || 'Course preview'}
          loading="lazy"
        />
      </div>
    );
  }

  if (item.videoUrl) {
    return (
      <div className="content-card-media-wrap">
        <video
          className="content-card-media"
          preload="metadata"
          muted
          playsInline
          onLoadedData={(event) => {
            try {
              event.currentTarget.currentTime = 0.1;
              event.currentTarget.pause();
            } catch {
              // No-op fallback for browsers that block seek at this point.
            }
          }}
        >
          <source src={item.videoUrl} />
        </video>
      </div>
    );
  }

  return null;
}

export default function PublicContentCard({ collectionKey, item, routeBase }) {
  const summary = summarizeContent(item, collectionKey);
  const detailLinkLabel = collectionKey === 'animations' ? 'View' : 'Read';

  return (
    <article className="content-card">
      <div className="content-card-header">
        <h3>{item.title}</h3>
        <span className={`status-pill status-${item.status || 'draft'}`}>
          {item.status || 'draft'}
        </span>
      </div>
      {collectionKey === 'articles' ? <p>By {item.author || 'No Author'}</p> : null}
      {collectionKey === 'animations' && item.creatorName ? (
        <p>Created by {item.creatorName}</p>
      ) : null}
      {collectionKey === 'animations' ? renderGalleryPreview(item) : null}
      {collectionKey === 'models' ? renderCourseContentPreview(item) : null}
      {summary ? <p>{summary}</p> : <p>No summary provided.</p>}
      <div className="content-card-footer">
        <span>{formatDate(item.updatedAt || item.createdAt)}</span>
        <Link to={`${routeBase}/${item.id}`}>{detailLinkLabel}</Link>
      </div>
    </article>
  );
}
